import React, { useReducer } from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { GridHeader, GridRow } from 'src/components/Reusable/Grid'
import { StickyGridHeader } from 'src/components/Reusable/Grid/Grid.styles'
import { EvidenceGridColumnSort } from 'src/components/Reusable/Grid/GridColumnSort/EvidenceGridColumnSort'
import { gridColSortReducerEvidence } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.utils'
import {
  GRID_COL_SORT_CONFIGS_EVIDENCE,
  GridColumnSortActionEvidence,
  GridColumnSortEvidence,
  GridColumnSortType,
} from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'
import { FlexAlign } from 'src/components/Reusable/Flex'
import { useUrlSearchParams } from 'src/Utils/helpers/navigation'
import {
  bannerHeight,
  filterBarHeight,
  headerContentHeight,
} from 'src/components/PageLayout/dimensionUtils'
import { useBannerContext } from 'src/context/Banner/BannerContext'
import { ModalFormId } from 'src/components/ModalForms'
import { GridColumnEdit } from 'src/components/Reusable/Grid/GridColumnEdit/GridColumnEdit'
import {
  EVIDENCE_HEADERS_TO_SORT_COL_WITH_DMS,
  EVIDENCE_INDEX_HEADER_TO_HEADER_VALUE,
  EvidenceIndexHeader,
} from '../evidence.constants'
import { useEvidenceIndexHeaders } from '../evidence.hooks'

export type EvidenceIndexGridHeaderProps = {
  isReadonly?: boolean
  showContentMatchColumn?: boolean
}

export const EvidenceIndexGridHeader = ({
  isReadonly = false,
  showContentMatchColumn,
}: EvidenceIndexGridHeaderProps): JSX.Element => {
  const { searchParams, updateUrlParams } = useUrlSearchParams()
  const headerConfig = useEvidenceIndexHeaders(isReadonly)
  const sortBy =
    (searchParams.sort_by && (searchParams.sort_by[0] as GridColumnSortType)) ||
    GridColumnSortType.DESCENDING
  const sortCol: GridColumnSortEvidence | undefined =
    (searchParams.sort_col &&
      (searchParams.sort_col[0] as GridColumnSortEvidence)) ||
    GridColumnSortEvidence.RELEVANT_DATE

  const [columnMapState, dispatch] = useReducer(
    gridColSortReducerEvidence,
    GRID_COL_SORT_CONFIGS_EVIDENCE(sortBy, sortCol),
  )

  const fetchSortedEvidence = (action: GridColumnSortActionEvidence) => {
    dispatch(action)
    updateUrlParams({ sort_by: [action.type], sort_col: [action.colName] })
  }

  return (
    <GridRow>
      {showContentMatchColumn && <GridHeader />}
      {Object.values(EvidenceIndexHeader).map(
        (header: EvidenceIndexHeader, idx: number) => {
          if (!headerConfig[header]) {
            return null
          }
          const { name, gridAlign } =
            EVIDENCE_INDEX_HEADER_TO_HEADER_VALUE[header]
          return (
            <EvidenceGridColumns
              key={`${name}-${idx}`}
              gridAlign={gridAlign}
              adjustForNavbar={!isReadonly}
            >
              {name}
              {!isReadonly && header === EvidenceIndexHeader.RELEVANT_DATE && (
                <GridColumnEdit
                  modalId={ModalFormId.BULK_UPDATE_EVIDENCE_RELEVANT_DATE}
                  capability={CAPABILITY.CAN_UPDATE_EVIDENCE_RELEVANT_DATE}
                />
              )}
              {EVIDENCE_HEADERS_TO_SORT_COL_WITH_DMS[header] && (
                <EvidenceGridColumnSort
                  type={
                    columnMapState[
                      EVIDENCE_HEADERS_TO_SORT_COL_WITH_DMS[header]
                    ].type
                  }
                  isActive={
                    columnMapState[
                      EVIDENCE_HEADERS_TO_SORT_COL_WITH_DMS[header]
                    ].isActive
                  }
                  colName={EVIDENCE_HEADERS_TO_SORT_COL_WITH_DMS[header]}
                  onSort={fetchSortedEvidence}
                />
              )}
            </EvidenceGridColumns>
          )
        },
      )}
    </GridRow>
  )
}

type EvidenceGridColumnsProps = React.PropsWithChildren<
  Omit<
    EvidenceIndexGridHeaderProps,
    'selectable' | 'showContentMatchColumn'
  > & {
    key: string
    gridAlign: FlexAlign
    adjustForNavbar?: boolean
  }
>

const EvidenceGridColumns = ({
  children,
  adjustForNavbar = false,
  key,
  gridAlign,
}: EvidenceGridColumnsProps) => {
  const { bannerCount } = useBannerContext()
  if (adjustForNavbar) {
    return (
      <StickyGridHeader
        key={key}
        $gridAlign={gridAlign}
        $top={
          headerContentHeight + filterBarHeight + bannerHeight * bannerCount
        }
      >
        {children}
      </StickyGridHeader>
    )
  }
  return (
    <StickyGridHeader key={key} $gridAlign={gridAlign}>
      {children}
    </StickyGridHeader>
  )
}
